import { CarItemSpecificationItemsType } from '@/interfaces/car.interfaces';
import type { ConstructorGridType } from '@/interfaces/constructor.interfaces';
import { ConstructorEnum } from '@/interfaces/constructor.interfaces';
import { II18Service } from '@/services/I18Service';
import InjectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import mergeGrid from '@/utils/mergeGrid';

export const DEFAULT_CAR_TILE_IDS_MAP = {
  CAR_TILE: 'car-tile-root',
  CAR_TILE_TAGS: 'car-tile-tags',
  CAR_TILE_MAIN: 'car-tile-main',
  CAR_TILE_IMAGES: 'car-tile-images',
  CAR_TILE_INFO: 'car-tile-info',
  CAR_TILE_LOCATION: 'car-tile-location',
  CAR_TILE_SPECIFICATION: 'car-tile-specification',
  CAR_TILE_HEADER: 'car-tile-header',
  CAR_TILE_STATUS: 'car-tile-status',
  CAR_TILE_FINANCE: 'car-tile-finance',
  CAR_TILE_LOCATION_AND_ACTIONS: 'car-tile-location-and-actions',
  CAR_TILE_FOOTER: 'car-tile-footer',
  CAR_TILE_PRICE: 'car-tile-price',
  CAR_TILE_PAYMENT: 'car-tile-payment',
  CAR_TILE_ACTIONS: 'car-tile-actions',
  CAR_TILE_GALLERY: 'car-tile-gallery',
};

/**
 * Блоки по умолчанию, размещающиеся на тайле автомобиля.
 */
export const GET_DEFAULT_CAR_TILE: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE,
    type: ConstructorEnum.CarTile,
    span: 24,
    gutter: [0, 'vertical-sky'],
    style: {
      height: '100%',
      alignContent: 'space-between',
    },
    props: {
      style: {
        minHeight: '400px',
      },
    },
    items: [
      {
        id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_TAGS,
        type: ConstructorEnum.CarTileTags,
        span: 24,
        order: 1,
        colStyle: {
          position: 'absolute',
          top: 10,
          left: 0,
          zIndex: 2,
          paddingLeft: 10,
          paddingRight: 10,
          width: '100%',
        },
        props: {},
        fields: ['logisticStatus', 'tags'],
      },
      {
        id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_MAIN,
        gutter: [0, 'vertical-blue'],
        span: 24,
        order: 3,
        items: [
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_GALLERY,
            type: ConstructorEnum.CarTileGallery,
            span: 24,
            order: 1,
            props: {},
            fields: ['images', 'media', 'model', 'thumbnails'],
          },
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_INFO,
            gutter: [0, 'vertical-sky'],
            span: 24,
            order: 3,
            items: [
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_HEADER,
                type: ConstructorEnum.CarTileHeader,
                span: 24,
                order: 1,
                props: {},
                fields: ['model', 'version', 'productionDate'],
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_SPECIFICATION,
                type: ConstructorEnum.CarTileSpecification,
                span: 24,
                order: 3,
                props: {},
                fields: ['engine', 'gearbox', 'drivetrain'],
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_STATUS,
                type: ConstructorEnum.CarTileStatus,
                span: 24,
                order: 5,
                props: {},
                fields: ['logisticStatus'],
              },
            ],
          },
        ],
      },
      {
        id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_FOOTER,
        gutter: [0, 'vertical-sky'],
        span: 24,
        order: 5,
        style: {
          flexDirection: 'column',
        },
        wrap: false,
        items: [
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_FINANCE,
            gutter: [18, 2],
            span: 24,
            order: 1,
            items: [
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_PRICE,
                type: ConstructorEnum.CarTilePrice,
                flex: 0,
                order: 1,
                props: {},
                fields: ['price', 'minPrice', 'benefits'],
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_PAYMENT,
                type: ConstructorEnum.CarTilePayment,
                flex: 0,
                order: 3,
                props: {},
                fields: ['model', 'price'],
              },
            ],
          },
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_LOCATION_AND_ACTIONS,
            gutter: [8, 0],
            span: 24,
            order: 3,
            align: 'middle',
            justify: 'space-between',
            wrap: false,
            items: [
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_LOCATION,
                type: ConstructorEnum.CarTileLocation,
                flex: 'auto',
                props: {},
                fields: ['locations'],
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_ACTIONS,
                type: ConstructorEnum.CarTileActions,
                flex: 'none',
                colStyle: {
                  marginLeft: 'auto',
                },
                props: {},
              },
            ],
          },
        ],
      },
    ],
  };
};

export const DEFAULT_COMPARISON_TILE_IDS_MAP = {
  COMPARISON_TILE: 'comparison-tile-root',
  COMPARISON_TILE_TAGS: 'comparison-tile-tags',
  COMPARISON_TILE_MAIN: 'comparison-tile-main',
  COMPARISON_TILE_INFO: 'comparison-tile-info',
  COMPARISON_TILE_HEADER: 'comparison-tile-header',
  COMPARISON_TILE_STATUS: 'comparison-tile-status',
  COMPARISON_TILE_FOOTER: 'comparison-tile-footer',
  COMPARISON_TILE_FINANCE: 'comparison-tile-finance',
  COMPARISON_TILE_PRICE_DESKTOP: 'comparison-tile-price-desktop',
  COMPARISON_TILE_PRICE_MOBILE: 'comparison-tile-price-mobile',
  COMPARISON_TILE_PAYMENT: 'comparison-tile-payment',
  COMPARISON_TILE_LOCATION_AND_ACTIONS_DESKTOP:
    'comparison-tile-location-and-actions-desktop',
  COMPARISON_TILE_LOCATION_AND_ACTIONS_MOBILE:
    'comparison-tile-location-and-actions-mobile',
  COMPARISON_TILE_LOCATION: 'comparison-tile-location',
  COMPARISON_TILE_ACTIONS: 'comparison-tile-actions',
  COMPARISON_TILE_GALLERY: 'comparison-tile-gallery',
};

/**
 * Блоки по умолчанию, размещающиеся на странице сравнения
 */
export const GET_DEFAULT_COMPARISON_TILE: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE,
    type: ConstructorEnum.CarTile,
    span: 24,
    gutter: [0, 'vertical-sky'],
    style: {
      height: '100%',
      alignContent: 'space-between',
    },
    props: {
      style: {
        minHeight: '280px',
      },
    },
    items: [
      {
        id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_TAGS,
        type: ConstructorEnum.CarTileTags,
        span: 24,
        order: 1,
        colStyle: {
          position: 'absolute',
          top: 10,
          left: 0,
          zIndex: 2,
          paddingLeft: 10,
          paddingRight: 10,
          width: '100%',
        },
        props: {},
        fields: ['logisticStatus', 'tags'],
      },
      {
        id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_MAIN,
        gutter: [0, 'vertical-blue'],
        span: 24,
        order: 3,
        items: [
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_GALLERY,
            type: ConstructorEnum.CarTileGallery,
            span: 24,
            order: 1,
            props: {},
            fields: ['images', 'media', 'model', 'thumbnails'],
          },
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_INFO,
            gutter: [0, 'vertical-blue'],
            span: 24,
            order: 3,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_HEADER,
                type: ConstructorEnum.CarTileHeader,
                span: 24,
                order: 1,
                fields: ['model', 'version', 'productionDate'],
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_STATUS,
                type: ConstructorEnum.CarTileStatus,
                span: 24,
                order: 5,
                props: {},
                fields: ['logisticStatus'],
              },
            ],
          },
        ],
      },
      {
        id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_FOOTER,
        gutter: [0, 'vertical-blue'],
        span: 24,
        order: 5,
        items: [
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_FINANCE,
            gutter: [18, 2],
            span: 24,
            order: 1,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_PRICE_DESKTOP,
                type: ConstructorEnum.CarTilePrice,
                xs: 0,
                sm: 24,
                order: 1,
                fields: ['price', 'minPrice', 'benefits'],
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_PRICE_MOBILE,
                type: ConstructorEnum.CarTilePrice,
                xs: 24,
                sm: 0,
                order: 1,
                props: {
                  oldValuePosition: 'bottom',
                },
                fields: ['price', 'minPrice', 'benefits'],
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_PAYMENT,
                type: ConstructorEnum.CarTilePayment,
                span: 24,
                order: 3,
                fields: ['model', 'price'],
              },
            ],
          },
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION_AND_ACTIONS_DESKTOP,
            gutter: [8, 8],
            span: 24,
            order: 3,
            align: 'middle',
            wrap: false,
            xs: 0,
            sm: 24,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION,
                type: ConstructorEnum.CarTileLocation,
                flex: 'auto',
                props: {},
                fields: ['locations'],
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_ACTIONS,
                type: ConstructorEnum.CarTileActions,
                flex: 'none',
                colStyle: {
                  marginLeft: 'auto',
                },
                props: {
                  isClickableComparison: false,
                },
              },
            ],
          },
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION_AND_ACTIONS_MOBILE,
            gutter: [8, 8],
            span: 24,
            order: 5,
            align: 'middle',
            xs: 24,
            sm: 0,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION,
                type: ConstructorEnum.CarTileLocation,
                span: 24,
                props: {},
                fields: ['locations'],
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_ACTIONS,
                type: ConstructorEnum.CarTileActions,
                span: 24,
                props: {
                  isClickableComparison: false,
                },
              },
            ],
          },
        ],
      },
    ],
  };
};

export const DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP = {
  COMPARISON_COMPACT_TILE: 'comparison-compact-tile-root',
  COMPARISON_COMPACT_TILE_IMAGES: 'comparison-compact-tile-images',
  COMPARISON_COMPACT_TILE_HEADER: 'comparison-compact-tile-header',
  COMPARISON_COMPACT_TILE_FINANCE: 'comparison-compact-tile-finance',
  COMPARISON_COMPACT_TILE_PRICE_DESKTOP: 'comparison-compact-tile-price-desktop',
  COMPARISON_COMPACT_TILE_PRICE_MOBILE: 'comparison-compact-tile-price-mobile',
  COMPARISON_COMPACT_TILE_PAYMENT: 'comparison-compact-tile-payment',
};

/**
 * Блоки по умолчанию, размещающиеся на компактном тайле автомобиля.
 */
export const GET_DEFAULT_COMPARISON_COMPACT_TILE: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP.COMPARISON_COMPACT_TILE,
    type: ConstructorEnum.CarCompactTile,
    span: 24,
    gutter: [0, 'vertical-blue'],
    style: {
      height: '100%',
      alignContent: 'space-between',
    },
    items: [
      {
        id: DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP.COMPARISON_COMPACT_TILE_IMAGES,
        type: ConstructorEnum.CarTileImages,
        span: 24,
        order: 1,
        colStyle: {
          maxWidth: '165px',
        },
        props: {},
        fields: ['images', 'media', 'model', 'thumbnails'],
      },
      {
        id: DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP.COMPARISON_COMPACT_TILE_HEADER,
        type: ConstructorEnum.CarTileHeader,
        span: 24,
        order: 3,
        fields: ['model', 'version', 'productionDate'],
      },
      {
        id: DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP.COMPARISON_COMPACT_TILE_FINANCE,
        gutter: [18, 2],
        span: 24,
        order: 5,
        items: [
          {
            id: DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP.COMPARISON_COMPACT_TILE_PRICE_DESKTOP,
            type: ConstructorEnum.CarTilePrice,
            xs: 0,
            sm: 24,
            order: 1,
            fields: ['price', 'minPrice', 'benefits'],
          },
          {
            id: DEFAULT_COMPARISON_COMPACT_TILE_IDS_MAP.COMPARISON_COMPACT_TILE_PRICE_MOBILE,
            type: ConstructorEnum.CarTilePrice,
            xs: 24,
            sm: 0,
            order: 1,
            props: {
              oldValuePosition: 'bottom',
            },
            fields: ['price', 'minPrice', 'benefits'],
          },
        ],
      },
    ],
  };
};

export const DEFAULT_CAR_CARD_IDS_MAP = {
  CAR_CARD: 'car-card-root',
  CAR_CARD_MAIN: 'car-card-main',
  CAR_CARD_IMAGES_WITH_MARKERS: 'car-card-images-with-markers',
  CAR_CARD_MARKERS: 'car-card-markers',
  CAR_CARD_IMAGES: 'car-card-images',
  CAR_CARD_INFO: 'car-card-info',
  CAR_CARD_SPECIFICATION: 'car-card-specification',
  CAR_CARD_LOCATION: 'car-card-location',
  CAR_CARD_FINANCE: 'car-card-finance',
  CAR_CARD_PRICE: 'car-card-price',
  CAR_CARD_PAYMENT: 'car-card-payment',
  CAR_CARD_BUTTONS: 'car-card-buttons',
  CAR_CARD_ACTIONS: 'car-card-actions',
  CAR_CARD_BOOKING: 'car-card-booking',
};

/**
 * Блоки по умолчанию, размещающиеся в блоке карточки автомобиля.
 */
export const GET_DEFAULT_CAR_CARD: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD,
    type: ConstructorEnum.CarCard,
    span: 24,
    gutter: [0, 'vertical-orange'],
    items: [
      {
        id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_MAIN,
        span: 24,
        order: 1,
        gutter: [{ xs: 0, lg: 32, xl: 36, xxl: 42 }, 'vertical-orange'],
        items: [
          {
            id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_IMAGES_WITH_MARKERS,
            xs: 24,
            lg: 12,
            xl: 14,
            gutter: [0, 'vertical-green'],
            style: { position: 'relative' },
            items: [
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_MARKERS,
                type: ConstructorEnum.CarCardMarkers,
                order: 1,
                gutter: [0, 'vertical-green'],
                colStyle: {
                  position: 'absolute',
                  zIndex: 1,
                  // Для организации корректных отступов относительно элемента
                  // который будет расположен ниже текущего.
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  borderWidth: '0px 12px',
                  paddingTop: '8px',
                  width: '100%',
                },
                props: {},
                fields: ['tags'],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_IMAGES,
                type: ConstructorEnum.CarCardImages,
                order: 3,
                props: {},
                fields: ['images', 'videos', 'media', 'model', 'thumbnails'],
              },
            ],
          },
          {
            id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_INFO,
            xs: 24,
            lg: 12,
            xl: 10,
            order: 9,
            gutter: [0, 'vertical-green'],
            items: [
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_SPECIFICATION,
                type: ConstructorEnum.CarCardSpecification,
                xs: { span: 24, order: 15 },
                sm: { span: 24, order: 11 },
                props: {},
                fields: ['options', 'specificationsValues'],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_LOCATION,
                type: ConstructorEnum.CarCardLocation,
                xs: { span: 24, order: 17 },
                sm: { span: 24, order: 13 },
                props: {},
                fields: ['locations'],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_FINANCE,
                xs: { span: 24, order: 11 },
                sm: { span: 24, order: 15 },
                gutter: [18, 'vertical-sky'],
                span: 24,
                items: [
                  {
                    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_PRICE,
                    type: ConstructorEnum.CarCardPrice,
                    xs: 24,
                    sm: 12,
                    props: {},
                    fields: ['price', 'minPrice', 'benefits'],
                  },
                  {
                    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_PAYMENT,
                    type: ConstructorEnum.CarCardPayment,
                    xs: 24,
                    sm: 12,
                    props: {},
                    fields: ['model', 'price'],
                  },
                ],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_BUTTONS,
                xs: { span: 24, order: 13 },
                sm: { span: 24, order: 17 },
                gutter: [18, 'vertical-sky'],
                span: 24,
                items: [
                  {
                    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_BOOKING,
                    type: ConstructorEnum.CarCardBooking,
                    xs: 24,
                    sm: 12,
                    props: {
                      icon: 'forward',
                      fullWidth: 'lg',
                    },
                  },
                ],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_ACTIONS,
                type: ConstructorEnum.CarCardActions,
                xs: { span: 24, order: 18 },
                sm: { span: 24, order: 18 },
                props: {},
              },
            ],
          },
        ],
      },
    ],
  };
};

export const DEFAULT_CAR_LIST_IDS_MAP = {
  CAR_LIST: 'car-list-root',
};

export const GET_DEFAULT_CAR_LIST: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST,
    type: ConstructorEnum.CarList,
    span: 24,
    items: [GET_DEFAULT_CAR_TILE()],
  };
};

export const GET_DEFAULT_FAVORITES_LIST: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST,
    type: ConstructorEnum.CarList,
    span: 24,
    items: [
      mergeGrid(GET_DEFAULT_CAR_TILE(), {
        items: [
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_ACTIONS,
            props: {
              isClickableFavorites: false,
            },
          },
        ],
      }),
    ],
  };
};

export const DEFAULT_MODEL_LIST_IDS_MAP = {
  MODEL_LIST: 'model-list-root',
};

export const GET_DEFAULT_MODEL_LIST: () => ConstructorGridType = () => {
  return {
    id: DEFAULT_MODEL_LIST_IDS_MAP.MODEL_LIST,
    type: ConstructorEnum.ModelList,
    span: 24,
    items: [],
  };
};

export const GET_DEFAULT_CAR_ITEM_SPECIFICATIONS: () => CarItemSpecificationItemsType =
  () => {
    const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

    return [
      { id: 'engine', key: 'engine', label: i18n.t('specification.engine') },
      { id: 'gearbox', key: 'gearbox', label: i18n.t('specification.gearbox') },
      { id: 'drivetrain', key: 'drivetrain', label: i18n.t('specification.drivetrain') },
      { id: 'version', key: 'version', label: i18n.t('specification.version') },
      { id: 'exterior', key: 'exterior', label: i18n.t('specification.exterior') },
      { id: 'interior', key: 'interior', label: i18n.t('specification.interior') },
      { id: 'vin', key: 'vin', label: i18n.t('specification.vin') },
    ];
  };

export const GET_DEFAULT_CAR_ITEM_SHORT_SPECIFICATIONS: () => CarItemSpecificationItemsType =
  () => [
    { id: 'engine', key: 'engine', icon: 'car-engine' },
    { id: 'gearbox', key: 'gearbox', icon: 'car-transmission' },
    { id: 'drivetrain', key: 'drivetrain', icon: 'car-drive' },
  ];

export const GET_DEFAULT_COMPARISON_ITEM_SPECIFICATIONS: () => CarItemSpecificationItemsType =
  () => {
    const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

    return [
      {
        id: 'engine',
        key: 'engine',
        path: ['engine'],
        formatter: values => {
          if (!Array.isArray(values)) {
            return values;
          }

          return values.filter(value => value !== null).join(', ');
        },
        label: i18n.t('specification.engine'),
      },
      { id: 'gearbox', path: 'gearbox', label: i18n.t('specification.gearbox') },
      { id: 'drivetrain', path: 'drivetrain', label: i18n.t('specification.drivetrain') },
      { id: 'version', path: 'version', label: i18n.t('specification.version') },
      {
        id: 'productionDate',
        path: 'productionDate',
        label: i18n.t('specification.productionDate'),
        formatter: value => {
          if (Array.isArray(value)) {
            return '';
          }

          const productionDate = new Date(value);

          return productionDate.getFullYear();
        },
      },
    ];
  };
